import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, FormHelperText, IconButton, Paper, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  RemoveCircleOutline as SkippedIcon,
  HourglassEmpty as PendingIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  PlayArrow as PlayArrowIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import api from '../../utils/api';
import JsonViewer from '../common/JsonViewer';

// Backend falls back to 60 s when config.timeout_seconds is missing or invalid.
const DEFAULT_TIMEOUT_SECONDS = 60;
// Extra client-side slack on top of the run timeout (cancel grace + response time).
const TIMEOUT_SLACK_SECONDS = 30;

const SOURCE_LABELS = {
  config: 'Saved example',
  input_schema: 'Generated from input schema',
  none: 'Empty (no example or schema)',
};
const RUN_STATUS_COLORS = { success: 'success', error: 'error', timeout: 'warning' };
const NODE_STATUS = {
  success: { color: 'success', icon: <CheckCircleIcon fontSize="small" color="success" /> },
  error: { color: 'error', icon: <ErrorIcon fontSize="small" color="error" /> },
  skipped: { color: 'default', icon: <SkippedIcon fontSize="small" color="disabled" /> },
  pending: { color: 'default', icon: <PendingIcon fontSize="small" color="disabled" /> },
};
const monoSx = { fontFamily: 'monospace', fontSize: '0.8125rem' };

const pretty = (value) => {
  if (value === undefined) return '';
  try { return JSON.stringify(value, null, 2); } catch { return String(value); }
};

const parseJson = (text) => {
  if (!text.trim()) return { value: null, error: 'Request body is empty; enter JSON (e.g. {})' };
  let value;
  try { value = JSON.parse(text); } catch (e) { return { value: undefined, error: e.message }; }
  // The backend treats a null body as "use the saved example", so null can't be tested as-is.
  if (value === null) return { value, error: 'null is not allowed as a test body; enter {} or another JSON value' };
  return { value, error: null };
};

const errorMessage = (error, fallback) => {
  if (error?.response?.status === 409) return 'This endpoint has no linked pipeline, so there is nothing to test.';
  if (error?.response?.status === 404) return 'Endpoint not found. It may have been deleted.';
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail) && detail.length) return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  if (error?.code === 'ECONNABORTED') return 'The request timed out waiting for the server';
  return error?.message || fallback;
};

const formatDuration = (ms) => {
  if (ms === null || ms === undefined) return '—';
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(2)} s`;
};
const formatCount = (n) => (n === null || n === undefined ? '—' : n);

const requestTimeoutMs = (config) => {
  const seconds = Number(config?.timeout_seconds);
  const runSeconds = Number.isFinite(seconds) && seconds > 0 ? seconds : DEFAULT_TIMEOUT_SECONDS;
  return (runSeconds + TIMEOUT_SLACK_SECONDS) * 1000;
};

const NodeRow = ({ node }) => {
  const [open, setOpen] = useState(false);
  const status = NODE_STATUS[node.status] || NODE_STATUS.pending;
  const hasPreview = node.output_preview !== null && node.output_preview !== undefined;
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          <IconButton size="small" aria-label={open ? `Hide output of ${node.name}` : `Show output of ${node.name}`}
            onClick={() => setOpen((o) => !o)}>
            {open ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center" gap={1}>
            {status.icon}
            <Chip label={node.status} color={status.color} size="small" variant="outlined" />
          </Box>
        </TableCell>
        <TableCell>{node.name}</TableCell>
        <TableCell><code>{node.module}</code></TableCell>
        <TableCell>{node.type}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatCount(node.records_in)} → {formatCount(node.records_out)}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDuration(node.duration_ms)}</TableCell>
        <TableCell sx={{ maxWidth: 240 }}>
          {node.error && <Typography variant="body2" color="error" sx={{ wordBreak: 'break-word' }}>{node.error}</Typography>}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell sx={{ py: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {node.status === 'skipped'
                  ? 'Data this node would have received (first records)'
                  : 'Output preview (first records)'}
              </Typography>
              {hasPreview ? <JsonViewer value={node.output_preview} maxHeight={240} />
                : <Typography variant="body2" color="text.secondary">No output preview available.</Typography>}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Runs an example request through the endpoint's real pipeline
 * (POST /endpoints/{id}/test) and shows input, per-node status and output.
 */
const EndpointTestDialog = ({ open, endpoint: endpointProp, onClose, onExampleSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  // The parent clears its endpoint as the dialog closes; keep showing the last
  // one so the title and alerts don't go blank during the close transition.
  const lastEndpointRef = useRef(endpointProp);
  if (endpointProp) lastEndpointRef.current = endpointProp;
  const endpoint = endpointProp || lastEndpointRef.current;
  const [bodyText, setBodyText] = useState('');
  const [exampleSource, setExampleSource] = useState(null);
  const [loadingExample, setLoadingExample] = useState(false);
  const [runOutputModules, setRunOutputModules] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef(null);
  // One AbortController per dialog session (open for a given endpoint). Starting a
  // new session or unmounting aborts the old one, so stale responses never set state.
  const sessionRef = useRef(null);

  const endpointId = endpoint?.id;
  const endpointConfig = endpoint?.config;
  const parsed = useMemo(() => parseJson(bodyText), [bodyText]);
  const jsonInvalid = parsed.error !== null;

  const stopTimer = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  useEffect(() => () => {
    stopTimer();
    if (sessionRef.current) sessionRef.current.abort();
  }, [stopTimer]);

  const loadExample = useCallback(async (signal) => {
    setLoadingExample(true);
    try {
      const response = await api.get(`/endpoints/${endpointId}/example`, { signal });
      if (signal.aborted) return;
      const example = response.data?.example;
      setBodyText(pretty(example === undefined || example === null ? {} : example));
      setExampleSource(response.data?.source || 'none');
    } catch (error) {
      if (signal.aborted || axios.isCancel(error)) return;
      setBodyText('{}');
      setExampleSource(null);
      setRequestError(errorMessage(error, 'Failed to load example'));
    } finally {
      if (!signal.aborted) setLoadingExample(false);
    }
  }, [endpointId]);

  useEffect(() => {
    if (!open || !endpointId) return;
    if (sessionRef.current) sessionRef.current.abort();
    const controller = new AbortController();
    sessionRef.current = controller;
    stopTimer();
    setBodyText('');
    setExampleSource(null);
    setResult(null);
    setRequestError(null);
    setRunOutputModules(false);
    setRunning(false);
    setSaving(false);
    setElapsed(0);
    loadExample(controller.signal);
  }, [open, endpointId, loadExample, stopTimer]);

  const handleRun = async () => {
    const controller = sessionRef.current;
    if (jsonInvalid || running || !controller) return;
    const { signal } = controller;
    setRunning(true); setResult(null); setRequestError(null); setElapsed(0);
    const started = Date.now();
    stopTimer();
    timerRef.current = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 250);
    try {
      const response = await api.post(
        `/endpoints/${endpointId}/test`,
        { body: parsed.value, run_output_modules: runOutputModules },
        // The shared instance defaults to 30 s; the run may take the endpoint's full timeout.
        { timeout: requestTimeoutMs(endpointConfig), signal }
      );
      if (signal.aborted) return;
      setResult(response.data);
    } catch (error) {
      if (signal.aborted || axios.isCancel(error)) return;
      setRequestError(errorMessage(error, 'Test run failed'));
    } finally {
      if (!signal.aborted) {
        stopTimer();
        setRunning(false);
      }
    }
  };

  const handleSaveExample = async () => {
    const controller = sessionRef.current;
    if (jsonInvalid || saving || !controller) return;
    const { signal } = controller;
    setSaving(true);
    try {
      // PUT /endpoints/{id} replaces config wholesale, so merge into the freshly fetched config.
      const current = await api.get(`/endpoints/${endpointId}`, { signal });
      const config = { ...(current.data?.config || {}), example_request: parsed.value };
      const response = await api.put(`/endpoints/${endpointId}`, { config }, { signal });
      if (signal.aborted) return;
      setExampleSource('config');
      enqueueSnackbar('Example saved', { variant: 'success' });
      if (onExampleSaved) onExampleSaved(response.data);
    } catch (error) {
      if (signal.aborted || axios.isCancel(error)) return;
      enqueueSnackbar(errorMessage(error, 'Failed to save example'), { variant: 'error' });
    } finally {
      if (!signal.aborted) setSaving(false);
    }
  };

  const handleClose = () => {
    if (running) return;
    // Drop in-flight example loads / saves so they can't update the hidden dialog.
    if (sessionRef.current) { sessionRef.current.abort(); sessionRef.current = null; }
    onClose();
  };
  const busy = running || loadingExample;
  const noPipeline = endpoint && !endpoint.pipeline_id;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth scroll="paper">
      <DialogTitle>
        Test endpoint{endpoint ? `: ${endpoint.name}` : ''}
        {endpoint && (
          <Typography variant="body2" color="text.secondary" component="div">
            <code>{endpoint.method?.toUpperCase()} /api/e{endpoint.path}</code>
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {noPipeline && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This endpoint has no linked pipeline, so there is nothing to test.
          </Alert>
        )}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle2">Request body (JSON)</Typography>
          {loadingExample ? <CircularProgress size={16} /> : exampleSource && (
            <Chip size="small" variant="outlined" label={SOURCE_LABELS[exampleSource] || exampleSource} data-testid="example-source" />
          )}
        </Box>
        <TextField
          value={bodyText}
          onChange={(e) => setBodyText(e.target.value)}
          multiline minRows={6} maxRows={18} fullWidth
          disabled={busy}
          error={jsonInvalid && !loadingExample}
          helperText={jsonInvalid && !loadingExample ? `Invalid JSON: ${parsed.error}` : ' '}
          inputProps={{ 'aria-label': 'Request body JSON', spellCheck: false, style: monoSx }}
        />
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2}>
          <Box sx={{ maxWidth: 560 }}>
            <FormControlLabel
              control={<Switch checked={runOutputModules} onChange={(e) => setRunOutputModules(e.target.checked)} disabled={busy} />}
              label="Run output modules"
            />
            <FormHelperText sx={{ mt: -0.5 }}>
              Off: only output modules are skipped and show the data they would have received. On: output
              modules run for real and may send or write data to external systems. Input, transform and AI
              modules always run for real (they may call external APIs or incur costs).
            </FormHelperText>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Button variant="outlined" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
              onClick={handleSaveExample} disabled={busy || saving || jsonInvalid || !endpointId}>
              Save as example
            </Button>
            <Button variant="contained"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
              onClick={handleRun} disabled={busy || jsonInvalid || !endpointId || noPipeline}>
              {running ? `Running… ${elapsed}s` : 'Run test'}
            </Button>
          </Box>
        </Box>

        {requestError && <Alert severity="error" sx={{ mt: 2 }}>{requestError}</Alert>}

        {result && (
          <Box mt={3} data-testid="endpoint-test-result">
            <Box display="flex" alignItems="center" gap={1.5} mb={1} flexWrap="wrap">
              <Typography variant="h6">Result</Typography>
              <Chip label={result.status} color={RUN_STATUS_COLORS[result.status] || 'default'} size="small" />
              <Typography variant="body2" color="text.secondary">
                {formatDuration(result.duration_ms)}
                {result.run_id ? ` · run #${result.run_id}` : ''}
                {result.execution_id ? ` · execution #${result.execution_id}` : ''}
              </Typography>
            </Box>
            {result.error && (
              <Alert severity={result.status === 'timeout' ? 'warning' : 'error'} sx={{ mb: 2 }}>{result.error}</Alert>
            )}
            <Typography variant="subtitle2" gutterBottom>Processing steps</Typography>
            {Array.isArray(result.nodes) && result.nodes.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" />
                      <TableCell>Status</TableCell>
                      <TableCell>Node</TableCell>
                      <TableCell>Module</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Records in → out</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.nodes.map((node, idx) => <NodeRow key={node.node_id ?? idx} node={node} />)}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>No node information was returned.</Typography>
            )}
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">Output</Typography>
            </Box>
            {result.output_truncated && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Output truncated by the server: only the first records are shown, or nothing if a single
                result was too large to return.
              </Alert>
            )}
            <Box mb={3}><JsonViewer key={result.execution_id} value={result.output ?? null} maxHeight={400} /></Box>
            <Typography variant="subtitle2" gutterBottom>Input used</Typography>
            <JsonViewer key={result.execution_id} value={result.input ?? null} maxHeight={240} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={running}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EndpointTestDialog;
